import { QuotaSource, ShortlistQuota } from '../interfaces/feature-count';

type QuotaSourceConsumption = {
  consumedCount: number;
  quotaSource: QuotaSource;
  remainingCount: number;
};

export function consumeQuotaSource(
  quotaSource: QuotaSource,
  requestedCount: number
): QuotaSourceConsumption {
  const normalizedRequestedCount = Math.max(requestedCount, 0);
  const consumedCount = Math.min(
    quotaSource.remaining,
    normalizedRequestedCount
  );
  const remaining = quotaSource.remaining - consumedCount;

  return {
    consumedCount,
    quotaSource: {
      ...quotaSource,
      count: Math.min(
        quotaSource.max - remaining,
        quotaSource.count + consumedCount
      ),
      remaining,
    },
    remainingCount: normalizedRequestedCount - consumedCount,
  };
}

function consumeCompanyQuotaSources(
  companyQuota: ShortlistQuota,
  requestedCount: number
) {
  if (companyQuota.isUnlimited) {
    return {
      quota: companyQuota,
      remainingCount: 0,
    };
  }

  const planBenefitConsumption = consumeQuotaSource(
    companyQuota.planBenefit,
    requestedCount
  );
  const addOnConsumption = consumeQuotaSource(
    companyQuota.addOn,
    planBenefitConsumption.remainingCount
  );
  const monthlyConsumption = consumeQuotaSource(
    companyQuota.monthly,
    addOnConsumption.remainingCount
  );

  const quota: ShortlistQuota = {
    ...companyQuota,
    planBenefit: planBenefitConsumption.quotaSource,
    addOn: addOnConsumption.quotaSource,
    monthly: monthlyConsumption.quotaSource,
    totalRemaining:
      planBenefitConsumption.quotaSource.remaining +
      addOnConsumption.quotaSource.remaining +
      monthlyConsumption.quotaSource.remaining,
  };

  return {
    quota,
    remainingCount: monthlyConsumption.remainingCount,
  };
}

type ConsumeShortlistQuotaSourcesParams = {
  shortlistCount: number;
  jobQuota?: QuotaSource;
  companyQuota?: ShortlistQuota;
};

export function consumeShortlistQuotaSources({
  shortlistCount,
  jobQuota,
  companyQuota,
}: ConsumeShortlistQuotaSourcesParams) {
  const jobConsumption = jobQuota
    ? consumeQuotaSource(jobQuota, shortlistCount)
    : {
        quotaSource: undefined,
        remainingCount: Math.max(shortlistCount, 0),
      };
  const companyShortlistCount = jobConsumption.remainingCount;
  const companyConsumption = companyQuota
    ? consumeCompanyQuotaSources(companyQuota, companyShortlistCount)
    : {
        quota: undefined,
        remainingCount: companyShortlistCount,
      };

  return {
    companyQuota: companyConsumption.quota,
    companyShortlistCount,
    jobQuota: jobConsumption.quotaSource,
    unconsumedCount: companyConsumption.remainingCount,
  };
}

function exhaustQuotaSource(quotaSource: QuotaSource): QuotaSource {
  return {
    ...quotaSource,
    count: quotaSource.max,
    remaining: 0,
  };
}

export function exhaustCompanyQuotaSources(
  companyQuota: ShortlistQuota
): ShortlistQuota {
  if (companyQuota.isUnlimited) {
    return companyQuota;
  }

  return {
    ...companyQuota,
    planBenefit: exhaustQuotaSource(companyQuota.planBenefit),
    addOn: exhaustQuotaSource(companyQuota.addOn),
    monthly: exhaustQuotaSource(companyQuota.monthly),
    totalRemaining: 0,
  };
}
